import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  TextField,
  Button,
  Stack,
  Breadcrumbs,
  Link,
  AppBar,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AddToolbar from "../../components/common/AddToolbar";
import Navbar from "../../components/common/Navbar";
import { useStyles } from "../../assets/styles/Theme";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import { PICKME_API_01 } from "../../api/ApiModel";
import { useSelector } from "react-redux";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants";

const SetupPickmeMetadata = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE, USERNAME } = SYS_USER.USER_PARAM || {};
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedItemDetails, setSelectedItemDetails] = useState({
    APPNAME: "",
    APPID: "",
    APPDESCRIPTION: "",
    CLIENTSECRET: "",
    REDIRECTURL: "",
    PRIVACYPOLICYURL: "",
    PRIMARYWEBHOOKURL: "",
    WEBHOOKSIGNINGKEY: "",
    WEBHOOKSECONDARYSIGNINGKEY: "",
    PUBLICDISPLAYNAME: "",
    PUBLICDISCRIPTION: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedItemDetails({
      ...selectedItemDetails,
      [name]: value,
    });
    if (errors[name] && value) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleConfirmClick = async () => {
    const requiredFields = [
      { name: "APPNAME", label: "Application Name" },
      { name: "APPID", label: "Application ID" },
      { name: "CLIENTSECRET", label: "Client Secret" },
      { name: "REDIRECTURL", label: "Redirect URL" },
    ];

    const missingFields = requiredFields.filter(
      (field) => !selectedItemDetails[field.name]
    );

    if (missingFields.length > 0) {
      const errorMessages = {};
      missingFields.forEach(
        (field) => (errorMessages[field.name] = `${field.label} is required.`)
      );
      setErrors(errorMessages);
      setSnackbarMessage("Please fill the required fields.");
      setOpenErrorSnackbar(true);
      return;
    }

    const payload = {
      COMPCODE: COMPCODE,
      LOCCODE: LOCCODE,
      RESTCODE: RESTCODE,
      ...selectedItemDetails,
      ENUSER: USERNAME,
      STS: CON_STATUS_ACTIVE,
    };

    try {
      const response = await externalpostApi(PICKME_API_01, payload);
      console.log("Success:", response);
      setOpenSnackbar(true);
      setSnackbarMessage("Successfully submitted!");
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error.response?.data?.message || "An error occurred while submitting.";
      setSnackbarMessage(errorMessage);
      setOpenErrorSnackbar(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await externalgetApi(PICKME_API_01, "get", {
          COMPCODE,
          LOCCODE,
          RESTCODE,
          STS: CON_STATUS_ACTIVE,
        });
        if (result) {
          setSelectedItemDetails({
            APPNAME: result[0].APPNAME || "",
            APPID: result[0].APPID || "",
            APPDESCRIPTION: result[0].APPDESCRIPTION || "",
            CLIENTSECRET: result[0].CLIENTSECRET || "",
            REDIRECTURL: result[0].REDIRECTURL || "",
            PRIVACYPOLICYURL: result[0].PRIVACYPOLICYURL || "",
            PRIMARYWEBHOOKURL: result[0].PRIMARYWEBHOOKURL || "",
            WEBHOOKSIGNINGKEY: result[0].WEBHOOKSIGNINGKEY || "",
            WEBHOOKSECONDARYSIGNINGKEY:
              result[0].WEBHOOKSECONDARYSIGNINGKEY || "",
            PUBLICDISPLAYNAME: result[0].PUBLICDISPLAYNAME || "",
            PUBLICDISCRIPTION: result[0].PUBLICDISCRIPTION || "",
          });
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  const handleClearClick = () => {
    setSelectedItemDetails({
      APPNAME: "",
      APPID: "",
      APPDESCRIPTION: "",
      CLIENTSECRET: "",
      REDIRECTURL: "",
      PRIVACYPOLICYURL: "",
      PRIMARYWEBHOOKURL: "",
      WEBHOOKSIGNINGKEY: "",
      WEBHOOKSECONDARYSIGNINGKEY: "",
      PUBLICDISPLAYNAME: "",
      PUBLICDISCRIPTION: "",
    });
    setErrors({});
  };

  const handleSnackbarClose = () => setOpenSnackbar(false);
  const handleErrorSnackbarClose = () => setOpenErrorSnackbar(false);

  return (
    <div>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs}
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Pickme</Link>
                <Link underline="none">Setup Metadata</Link>
              </Breadcrumbs>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.AI_AddStyledBox}>
        <AddToolbar label={{ name: "Setup Pickme Metadata" }} />
        <Grid container spacing={2} sx={styles.AI_container}>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="App Name"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.APPNAME}
              onChange={handleInputChange}
              name="APPNAME"
              error={Boolean(errors.APPNAME)}
              helperText={errors.APPNAME}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="App ID"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.APPID}
              onChange={handleInputChange}
              name="APPID"
              error={Boolean(errors.APPID)}
              helperText={errors.APPID}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="App Description"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.APPDESCRIPTION}
              onChange={handleInputChange}
              name="APPDESCRIPTION"
              error={Boolean(errors.APPDESCRIPTION)}
              helperText={errors.APPDESCRIPTION}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Client Secret"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.CLIENTSECRET}
              onChange={handleInputChange}
              name="CLIENTSECRET"
              error={Boolean(errors.CLIENTSECRET)}
              helperText={errors.CLIENTSECRET}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Redirect URL"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.REDIRECTURL}
              onChange={handleInputChange}
              name="REDIRECTURL"
              error={Boolean(errors.REDIRECTURL)}
              helperText={errors.REDIRECTURL}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Privacy Policy URL"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.PRIVACYPOLICYURL}
              onChange={handleInputChange}
              name="PRIVACYPOLICYURL"
              error={Boolean(errors.PRIVACYPOLICYURL)}
              helperText={errors.PRIVACYPOLICYURL}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Primary Webhook URL"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.PRIMARYWEBHOOKURL}
              onChange={handleInputChange}
              name="PRIMARYWEBHOOKURL"
              error={Boolean(errors.PRIMARYWEBHOOKURL)}
              helperText={errors.PRIMARYWEBHOOKURL}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Webhook Signing Key"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.WEBHOOKSIGNINGKEY}
              onChange={handleInputChange}
              name="WEBHOOKSIGNINGKEY"
              error={Boolean(errors.WEBHOOKSIGNINGKEY)}
              helperText={errors.WEBHOOKSIGNINGKEY}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Webhook Secondary Signing Key"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.WEBHOOKSECONDARYSIGNINGKEY}
              onChange={handleInputChange}
              name="WEBHOOKSECONDARYSIGNINGKEY"
              error={Boolean(errors.WEBHOOKSECONDARYSIGNINGKEY)}
              helperText={errors.WEBHOOKSECONDARYSIGNINGKEY}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Public Display Name"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.PUBLICDISPLAYNAME}
              onChange={handleInputChange}
              name="PUBLICDISPLAYNAME"
              error={Boolean(errors.PUBLICDISPLAYNAME)}
              helperText={errors.PUBLICDISPLAYNAME}
            />
          </Grid>
          <Grid item lg={6} md={6} xs={12} sm={12}>
            <TextField
              label="Public Description"
              size="small"
              fullWidth
              variant="outlined"
              autoComplete="off"
              value={selectedItemDetails.PUBLICDISCRIPTION}
              onChange={handleInputChange}
              name="PUBLICDISCRIPTION"
              error={Boolean(errors.PUBLICDISCRIPTION)}
              helperText={errors.PUBLICDISCRIPTION}
            />
          </Grid>
        </Grid>

        <Stack
          direction="row"
          spacing={2}
          m={1}
          justifyContent="flex-end"
          padding={1}
        >
          <Button
            variant="outlined"
            sx={styles.AI_button1}
            onClick={handleClearClick}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            sx={styles.AI_button2}
            onClick={handleConfirmClick}
          >
            Confirm
          </Button>
        </Stack>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="success" onClose={handleSnackbarClose}>
          Metadata saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={5000}
        onClose={handleErrorSnackbarClose}
      >
        <Alert severity="error" onClose={handleErrorSnackbarClose}>
          {snackbarMessage
            ? snackbarMessage
            : "Please check the errors before submitting."}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SetupPickmeMetadata;
